import { Check, FileText, Upload, X } from "lucide-react"

import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"
import { bundleComposition, bundleDeliverables } from "@/lib/post/bundle"

// BundleDeliverables — the bundle-only stage between the configured services
// (book_writing + cover_design) and the add-on step. It PRESENTS what the bundle
// includes (value cards) and collects the only remaining REAL inputs the backend
// needs that aren't already configured:
//   - outline collaboration (Platinum / Narration — outline__three `collaborative`)
//   - narration voice count (Audiobook Narration + fiction — narration__one)
//   - the 3 optional requirement uploads (bundle_*_file)
//
// Props:
//   state   — the wizard state (reads bundleTier, postings.book_writing, bundleExtras)
//   ctx     — the wizard ctx (rates + outlinePrices); the value-card struck prices read
//             ctx.rates?.X ?? RATES.X so they trace to the customer's PriceList (C2).
//             Absent → RATES seed fallback keeps first paint working.
//   setExtra(key, value)  — write one bundleExtras field
//   setFile(slot, file)   — write one bundleExtras.files slot (cover|description|narration)
//   showIncluded — render the "In your bundle" value-card list (default true). The
//                  Set up hub lists the same deliverables as queue rows, so it
//                  mounts this with showIncluded={false} to avoid duplicating them.

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`

function ValueCard({ item }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-card px-4 py-3.5">
      <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-[var(--green-soft,var(--secondary))] text-done">
        <Check className="size-3.5" strokeWidth={3} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h4 className="font-display text-[13.5px] font-bold tracking-tight text-foreground">{item.title}</h4>
          <span className="ml-auto whitespace-nowrap text-[11.5px] font-bold text-done">
            {item.priced ? "Included · priced by length" : "Included"}
            {!item.priced && item.value ? (
              <span className="ml-1 font-semibold text-muted-foreground line-through">{fmt(item.value)}</span>
            ) : null}
          </span>
        </div>
        {item.note && <p className="mt-1 text-[12px] leading-snug text-muted-foreground text-pretty">{item.note}</p>}
      </div>
    </div>
  )
}

// Per-slot accept lists mirror the server (Plumbline bug 8): the cover slot
// runs valid_image_mime_type (images allowed), description + narration run
// valid_requirement_mime_type (documents only). `.doc` is valid for the
// requirement slots (ruling C1) but NOT the image slot (VALID_IMAGE_FILE_TYPES
// carries no application/msword).
const COVER_ACCEPT = ".docx,.pdf,.zip,.jpg,.jpeg,.png,.gif"
const DOC_ACCEPT = ".doc,.docx,.pdf,.zip"

const STATUS_LABELS = {
  waiting: "Waiting to upload",
  uploading: "Uploading…",
  saved: "Saved",
  failed: "Upload failed",
}

function FileSlot({
  label,
  help,
  accept = DOC_ACCEPT,
  file,
  status,
  onPick,
  onClear,
  onRetry,
  retrying,
  fieldKey,
}) {
  const chosen = file && file.name ? file : null
  const pendingFile = typeof File !== "undefined" && chosen instanceof File
  const visibleStatus = status || (pendingFile ? "waiting" : chosen ? "saved" : null)
  const supported = accept.split(",").join(", ")
  return (
    <div className="flex min-w-0 flex-col gap-1.5" data-post-field={fieldKey}>
      <div className="text-[12.5px] font-bold text-foreground">
        {label} <span className="ml-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">Optional</span>
      </div>
      {help && <p className="-mt-0.5 text-[12px] leading-snug text-muted-foreground">{help}</p>}
      {chosen ? (
        <div className="flex min-w-0 items-center gap-3 rounded-[10px] border border-border bg-card px-3.5 py-2.5">
          <span className="flex size-8 items-center justify-center rounded-lg bg-secondary">
            <FileText className="size-4 text-muted-foreground" strokeWidth={1.9} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12.5px] font-semibold text-foreground">{chosen.name}</span>
            {visibleStatus && (
              <span
                aria-live="polite"
                className={cn(
                  "block text-[11px] font-semibold",
                  visibleStatus === "failed" ? "text-destructive" : "text-muted-foreground"
                )}
              >
                {STATUS_LABELS[visibleStatus]}
              </span>
            )}
          </span>
          {visibleStatus === "failed" && (
            <button
              type="button"
              onClick={onRetry}
              disabled={retrying}
              className="shrink-0 whitespace-nowrap text-[11.5px] font-semibold text-primary underline underline-offset-2 disabled:opacity-50"
            >
              Retry upload
            </button>
          )}
          <button
            type="button"
            aria-label="Remove file"
            onClick={onClear}
            className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="size-3.5" strokeWidth={2} />
          </button>
        </div>
      ) : (
        <label className="flex cursor-pointer flex-col items-center gap-1.5 rounded-[11px] border border-dashed border-line-2 px-4 py-5 text-center transition-colors hover:border-ink-3 hover:bg-accent">
          <Upload className="size-5 text-muted-foreground" strokeWidth={1.8} />
          <span className="text-[12.5px] font-semibold text-muted-foreground">Attach references</span>
          <span className="text-[11px] text-muted-foreground/80">Supported: {supported}</span>
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files && e.target.files[0]
              if (f) onPick(f)
            }}
          />
        </label>
      )}
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">{title}</div>
      {children}
    </div>
  )
}

export function BundleDeliverables({
  state,
  ctx = {},
  setExtra,
  setFile,
  uploadStatuses = {},
  retryUpload,
  retryingUpload = false,
  showIncluded = true,
}) {
  const tier = state.bundleTier
  const comp = bundleComposition(tier)
  const bw = (state.postings && state.postings.book_writing) || {}
  const fiction = comp.forceFiction || !!bw.fiction
  // AI-manuscript bundles never spawn a description order (the AI fan-out skips
  // create_bundle_book_description), so uploads to the description slot would
  // strand with no freelancer destination — hide it (Plumbline ruling C5).
  const aiBundle = !!bw.use_ai
  const extras = state.bundleExtras || {}
  const files = extras.files || {}
  const items = bundleDeliverables(state, ctx)

  const collaborative = extras.collaborative === false ? false : true
  const voiceCount = extras.character_voice_count
  const voiceMissing = comp.narration && fiction && !(Number(voiceCount) >= 1)

  return (
    <div className="flex flex-col gap-6">
      {showIncluded && (
        <Section title="In your bundle">
          <div className="flex flex-col gap-2.5">
            {items.map((item) => (
              <ValueCard key={item.key} item={item} />
            ))}
          </div>
        </Section>
      )}

      {comp.outlineChild && (
        <Section title="Outline">
          <div className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3.5" data-post-field="collaborative">
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-foreground">Work on the outline together</div>
              <p className="-mt-0.5 text-[12px] leading-snug text-muted-foreground">
                Collaborate with your outline writer and leave feedback as it takes shape. Turn this off to have them
                deliver the finished outline for your review. Up to 2 revisions either way.
              </p>
            </div>
            <Switch checked={collaborative} onCheckedChange={(v) => setExtra("collaborative", v)} />
          </div>
        </Section>
      )}

      {comp.narration && fiction && (
        <Section title="Audiobook narration">
          <div className="flex flex-col gap-1.5" data-post-field="character_voice_count">
            <div className="text-[12.5px] font-bold text-foreground">
              Number of distinct character voices{" "}
              <span className="ml-1 rounded-full bg-[var(--red-soft)] px-2 py-0.5 text-[11px] font-semibold text-primary">Required</span>
            </div>
            <p className="-mt-0.5 text-[12px] leading-snug text-muted-foreground">
              How many characters need a distinct voice from your narrator?
            </p>
            <Input
              id="f-character_voice_count"
              type="number"
              min={1}
              step={1}
              className="max-w-[160px]"
              value={voiceCount ?? ""}
              aria-invalid={voiceMissing}
              onChange={(e) => setExtra("character_voice_count", e.target.value === "" ? "" : Number(e.target.value))}
            />
          </div>
        </Section>
      )}

      {/* The design's separate "Bundle materials · optional" card (audit G): its
          own header + copy, with per-slot labels in the design/legacy idiom
          ("Attach requirements for Cover design"). The per-slot accept lists
          (cover = images + docs, description/narration = docs only) and the
          AI-bundle description-slot hide (ruling C5) are the shipped Phase-1
          parity fixes, preserved here. */}
      <div className="flex flex-col gap-3">
        <div>
          <h3 className="font-display text-[15px] font-bold tracking-tight text-foreground">
            Bundle materials <span className="text-[12px] font-semibold text-muted-foreground">· optional</span>
          </h3>
          <p className="mt-1 text-[12px] leading-snug text-muted-foreground text-pretty">
            Have references or source files for the bundle's other deliverables? Attach them now so your team has
            everything up front, or add them later from the order.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <FileSlot
            fieldKey="bundle_cover_file"
            label="Attach requirements for Cover design"
            accept={COVER_ACCEPT}
            file={files.cover}
            status={uploadStatuses.cover}
            onPick={(f) => setFile("cover", f)}
            onClear={() => setFile("cover", null)}
            onRetry={retryUpload}
            retrying={retryingUpload}
          />
          {comp.description && !aiBundle && (
            <FileSlot
              fieldKey="bundle_description_file"
              label={`Attach requirements for ${fiction ? "Fiction blurb" : "Book description"}`}
              accept={DOC_ACCEPT}
              file={files.description}
              status={uploadStatuses.description}
              onPick={(f) => setFile("description", f)}
              onClear={() => setFile("description", null)}
              onRetry={retryUpload}
              retrying={retryingUpload}
            />
          )}
          {comp.narration && (
            <FileSlot
              fieldKey="bundle_narration_file"
              label="Attach requirements for Audiobook narration"
              accept={DOC_ACCEPT}
              file={files.narration}
              status={uploadStatuses.narration}
              onPick={(f) => setFile("narration", f)}
              onClear={() => setFile("narration", null)}
              onRetry={retryUpload}
              retrying={retryingUpload}
            />
          )}
        </div>
      </div>
    </div>
  )
}
